<template>
  <div
    v-if="isAdmin || isSupervisor || isDirector || isTeamLead"
    id="management"
    class="page-content management"
  >
    <div class="row">
      <div class="col-sm-6">
        <remove-all-payments />
        <clear-user-fraud />
        <send-email-or-automated-call />
        <send-reactivation-email />
        <update-customer-password />
        <send-robo-call />
        <send-bulk-email />
        <send-bulk-sms />
        <send-bulk-a-i-sms />
        <upload-user-document />
        <ban-caller-car-type />
        <url-shortener />
        <send-promotion-email-or-sms />
        <remove-customer-permanently />
      </div>

      <div class="col-sm-6">
        <transfer-ride-credits />
        <mark-ride />
        <unmark-ride />
        <do-not-charge-ride />
        <unmark-do-not-charge-ride />
        <submit-blacklist />
        <add-referral-code />
        <transfer-ride />
        <partner-individual-charge />
        <partner-caller-credits-charge />
        <charge-invoice />
        <apply-manual-subsidy />
        <free-trial-reminder />
        <account-deactivation-request-reminder />
        <outbound-call-queue v-if="isAdmin || isDirector || isSupervisor" />
        <SlingTeamLeadSync />
      </div>
    </div>
    <update-partner-subsidy-locations />
    <blacklist-address />
    <closed-accounts />
    <driver-search />
    <car-types />
    <announcements />
    <operator-report-form-stack-search />
    <grant-requests v-if="isAdmin" />
    <twilio-numbers />
    <registration-urls />
    <plan-trials v-if="isAdmin || isDirector" />
    <email-templates v-if="isAdmin || isDirector" />
  </div>

  <div
    v-else
    class="page-content management"
  >
    <div class="row">
      <div class="col-sm-6">
        <mark-ride />
        <do-not-charge-ride v-if="isLossPrevention" />
        <clear-user-fraud />
      </div>

      <div class="col-sm-6">
        <unmark-ride />
        <partner-individual-charge />
        <charge-invoice />
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <driver-search />
        <update-partner-subsidy-locations />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { nextTick, toRefs, onMounted, defineAsyncComponent } from 'vue';
import { useRoute } from 'vue-router';

const SubmitBlacklist = defineAsyncComponent(() => import('./Management/SubmitBlacklist.vue') as any);
const BlacklistAddress = defineAsyncComponent(() => import('./Management/BlacklistAddress.vue') as any);
const CarTypes = defineAsyncComponent(() => import('./Management/CarTypes.vue') as any);
const ClosedAccounts = defineAsyncComponent(() => import('./Management/ClosedAccounts.vue') as any);
const TransferRide = defineAsyncComponent(() => import('./Management/TransferRide.vue') as any);
const SendEmailOrAutomatedCall = defineAsyncComponent(() => import('./Management/SendEmailOrAutomatedCall.vue') as any);
const SendReactivationEmail = defineAsyncComponent(() => import('./Management/SendReactivationEmail.vue') as any);
const RemoveAllPayments = defineAsyncComponent(() => import('./Management/RemoveAllPayments.vue') as any);
const ClearUserFraud = defineAsyncComponent(() => import('./Management/ClearUserFraud.vue') as any);
const MarkRide = defineAsyncComponent(() => import('./Management/MarkRide.vue') as any);
const UnmarkRide = defineAsyncComponent(() => import('./Management/UnmarkRide.vue') as any);
const AddReferralCode = defineAsyncComponent(() => import('./Management/AddReferralCode.vue') as any);
const TransferRideCredits = defineAsyncComponent(() => import('./Management/TransferRideCredits.vue') as any);
const UpdateCustomerPassword = defineAsyncComponent(() => import('./Management/UpdateCustomerPassword.vue') as any);
const DoNotChargeRide = defineAsyncComponent(() => import('./Management/DoNotChargeRide.vue') as any);
const UnmarkDoNotChargeRide = defineAsyncComponent(() => import('./Management/UnmarkDoNotChargeRide.vue') as any);
const UpdatePartnerSubsidyLocations = defineAsyncComponent(() => import('./Management/UpdatePartnerSubsidyLocations.vue') as any);
const PartnerIndividualCharge = defineAsyncComponent(() => import('./Management/PartnerIndividualCharge.vue') as any);
const ApplyManualSubsidy = defineAsyncComponent(() => import('./Management/ApplyManualSubsidy.vue') as any);
const PartnerCallerCreditsCharge = defineAsyncComponent(() => import('./Management/PartnerCallerCreditsCharge.vue') as any);
const SendRoboCall = defineAsyncComponent(() => import('./Management/SendRoboCall.vue') as any);
const SendBulkEmail = defineAsyncComponent(() => import('./Management/SendBulkEmail.vue') as any);
const SendBulkSms = defineAsyncComponent(() => import('./Management/SendBulkSms.vue') as any);
const SendBulkAISms = defineAsyncComponent(() => import('./Management/SendBulkAISms.vue') as any);
const ChargeInvoice = defineAsyncComponent(() => import('./Management/ChargeInvoice.vue') as any);
const BanCallerCarType = defineAsyncComponent(() => import('./Management/BanCallerCarType.vue') as any);
const DriverSearch = defineAsyncComponent(() => import('./Management/DriverSearch.vue') as any);
const AccountDeactivationRequestReminder = defineAsyncComponent(() => import('./Management/AccountDeactivationRequestReminder.vue') as any);
const FreeTrialReminder = defineAsyncComponent(() => import('./Management/FreeTrialReminder.vue') as any);
const OperatorReportFormStackSearch = defineAsyncComponent(() => import('./Management/OperatorReportFormStackSearch.vue') as any);
const UploadUserDocument = defineAsyncComponent(() => import('./Management/UploadUserDocument.vue') as any);
const Announcements = defineAsyncComponent(() => import('./Management/Announcements.vue') as any);
const UrlShortener = defineAsyncComponent(() => import('./Management/UrlShortener.vue') as any);
const RemoveCustomerPermanently = defineAsyncComponent(() => import('./Management/RemoveCustomerPermanently.vue') as any);
const GrantRequests = defineAsyncComponent(() => import('./Management/GrantRequests.vue') as any);
const SendPromotionEmailOrSms = defineAsyncComponent(() => import('./Management/SendPromotionEmailOrSms.vue') as any);

const OutboundCallQueue = defineAsyncComponent(() => import('./Management/OutboundCallQueue.vue') as any);
const TwilioNumbers = defineAsyncComponent(() => import('./Management/TwilioNumbers.vue') as any);
const RegistrationUrls = defineAsyncComponent(() => import('./Management/RegistrationUrls.vue') as any);
const PlanTrials = defineAsyncComponent(() => import('./Management/PlanTrials.vue') as any);
const EmailTemplates = defineAsyncComponent(() => import('./Management/EmailTemplates.vue') as any);
const SlingTeamLeadSync = defineAsyncComponent(() => import('./Management/SlingTeamLeadSync.vue') as any);

import util from '@/Utils/util';
import { useProfile } from '@/use/profile';

const route = useRoute();

const { userRoles } = useProfile();
const { isAdmin, isSupervisor, isDirector, isTeamLead, isLossPrevention } = toRefs(userRoles);

onMounted(async () => {
  await nextTick();
  util.moveScrollComponent(route.hash);
});
</script>

<style lang="scss">
.management {
  .refund-individual-charge,
  .refund-ride-costs {
    .refund-content {
      .row {
        margin-bottom: 0;
      }
      .highlight {
        background: #e1e1e1;
        padding: 2px 0;
      }
      .cost-value-section {
        button.prefill-action {
          min-width: unset;
          font-size: 20px !important;
          padding: 3px 10px !important;
        }
        .customer-name,
        .partner-name {
          font-size: 18px;
          height: fit-content;
          line-height: unset;
          padding-left: 10px;
        }
      }
      .cost-value {
        color: #b33e3e;
        font-size: 16px;
        height: 40px;
        line-height: 40px;
      }
    }
  }
}

.management {
  .account-deactivation-request-reminder,
  .free-trial-reminder {
    .form-submit-button-group {
      .btn {
        margin: 10px !important;
      }
    }
  }
}
</style>
